/* pendingQuestion — carries a guest's blocked question across the sign-up detour.
 *
 * A guest gets a few free AI questions, then the mascot pop-up asks them to
 * create an account (see GuestSignupGate.jsx). The question they had just
 * typed would normally be lost in that detour. This remembers it, and where
 * they were, so that after they sign up they land back on the same page with
 * the question sent for them — the wall should feel like a door that opens,
 * not a slap on the way in.
 *
 * Same shape and reasoning as pendingCheckout.js: async storage rather than
 * router state, because the detour can cross a full page load (Signup's own
 * "Sign in" link goes to /login). Short-lived on purpose, so a stale question
 * never fires minutes later for an unrelated reason.
 *
 * Two keys, two lifetimes:
 *   - jai_pending_question: the text + page. Read by the page that owns the
 *     question box (ChatPage) once they are back.
 *   - The path lives in the same record; Signup reads it to know where to send
 *     the user instead of /pricing.
 */

import { storage } from '../services/storage'

const KEY = 'jai_pending_question'
const MAX_AGE_MS = 30 * 60 * 1000
const MAX_TEXT = 4000 // matches MAX_MESSAGE_LENGTH in chatController.js

export async function rememberPendingQuestion({ path, question }) {
  try {
    await storage.setItem(KEY, JSON.stringify({
      path: path || '/chat',
      question: typeof question === 'string' ? question.slice(0, MAX_TEXT) : '',
      at: Date.now(),
    }))
  } catch {
    // Storage unavailable (private mode) — they just retype it. Never throw
    // from here: it runs on the way to the sign-up page.
  }
}

async function read() {
  try {
    const raw = await storage.getItem(KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.at || Date.now() - parsed.at > MAX_AGE_MS) return null
    return parsed
  } catch {
    return null
  }
}

/** Where to send someone who just created an account after hitting the wall,
 *  or null if they did not arrive via the wall. Does NOT clear the record —
 *  the destination page still needs the question. */
export async function peekPendingQuestionPath() {
  const pending = await read()
  // Only same-app paths: this ends up in navigate(), so refuse anything else.
  return pending && typeof pending.path === 'string' && pending.path.startsWith('/')
    ? pending.path
    : null
}

/** Reads the saved question AND clears it — call once, from the page that will
 *  send it. Returns '' when there is nothing (or it is stale). */
export async function consumePendingQuestion() {
  const pending = await read()
  try { await storage.removeItem(KEY) } catch { /* nothing to clean up */ }
  return pending?.question || ''
}
